import { useState } from "react";

import { Button, Divider, Flex, Textarea } from "@aircall/tractor";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";

import { useDeleteEvaluation } from "@/hooks/useDeleteEvaluation/useDeleteEvaluation";
import { EvaluationAccordionHeaderContent } from "../EvaluationAccordionHeaderContent";
import { EvaluationHeading } from "../EvaluationHeading";
import { EvaluationQuestionSummary } from "../EvaluationQuestionSummary";
import {
  isBinaryEvaluationQuestion,
  isRatingEvaluationQuestion,
  transformFormValueIntoInput,
} from "../EvaluationUpdateForm";

import { Accordion } from "@/components/Accordion";
import { AccordionBody } from "@/components/Accordion/components/AccordionBody";
import { AccordionHeader } from "@/components/Accordion/components/AccordionHeader";
import { AccordionItem } from "@/components/Accordion/components/AccordionItem";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import {
  BinaryAnswerValue,
  Evaluation,
  RatingAnswerValue,
} from "@/graphql/generated/graphql";
import { Unpacked } from "@/helpers/types/types.helpers";
import { useBoolean } from "@/hooks/useBoolean/useBoolean";

import { useEvaluation } from "@/hooks/useEvaluation/useEvaluation";
import styled from "@xstyled/styled-components";
import {
  EvaluationAccordionProps,
  ExcludeNotApplicable,
  QuestionBinaryProps,
  QuestionMultipleProps,
} from ".";

import { useCall } from "@/hooks/useCall/useCall";
import { useHasCcAccess } from "@/hooks/useHasCcAccess/useHasCcAccess";
import { useUpdateEvaluation } from "@/hooks/useUpdateEvaluation/useUpdateEvaluation";
import { getRevieweeUserId } from "../EvaluationNew/EvaluationNew.utils";
import { useTracking } from "./EvaluationResult.hooks";

const StyledAccordionItem = styled(AccordionItem)`
  &:last-child {
    border-bottom: none;
  }
`;

function MarkAsReviewedForm({
  onSubmit,
  submitting,
}: {
  onSubmit: (feedback: string) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState("");
  const onSubmitClick = () => {
    onSubmit(feedback.trim());
  };
  // render a textarea of global feedback and a button to mark as reviewed
  return (
    <Flex flexDirection="column" gap="xxs" alignItems="flex-end" px="m" pb="m">
      <Textarea
        name="feedback"
        value={feedback}
        placeholder="Add overall feedback here"
        onChange={(e) => setFeedback(e.target.value)}
      />
      <Button onClick={onSubmitClick} disabled={submitting}>
        {t("evaluation.mark_as_reviewed")}
      </Button>
    </Flex>
  );
}

function QuestionBinary({ question }: Readonly<QuestionBinaryProps>) {
  const { binaryAnswer } = question;

  return (
    <EvaluationQuestionSummary<ExcludeNotApplicable<BinaryAnswerValue>>
      key={question.ID}
      value={
        binaryAnswer === "NOT_APPLICABLE" || !binaryAnswer
          ? undefined
          : binaryAnswer
      }
      question={question.questionText}
      rank={question.questionOrder + 1}
      isNotApplicable={binaryAnswer === "NOT_APPLICABLE"}
      comment={question.feedback ?? ""}
      maximumPoints={question.score.totalPoints}
      points={question.score.scorePoints}
      p="m"
    />
  );
}

function QuestionMultiple({ question }: Readonly<QuestionMultipleProps>) {
  const { ratingAnswer } = question;

  return (
    <EvaluationQuestionSummary<ExcludeNotApplicable<RatingAnswerValue>>
      key={question.ID}
      value={
        ratingAnswer === "NOT_APPLICABLE" || !ratingAnswer
          ? undefined
          : ratingAnswer
      }
      question={question.questionText}
      rank={question.questionOrder + 1}
      isNotApplicable={ratingAnswer === "NOT_APPLICABLE"}
      comment={question.feedback ?? ""}
      maximumPoints={question.score.totalPoints}
      points={question.score.scorePoints}
      p="m"
    />
  );
}

function EvaluationAccordion({ evaluation }: EvaluationAccordionProps) {
  const [openedItems, setOpenedItems] = useState<
    Set<Unpacked<Evaluation["categories"]>["ID"]>
  >(() => new Set([]));

  const handleOnClick = (id: string) => () => {
    setOpenedItems((prevSet) => {
      const newSet = new Set(prevSet);

      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      return newSet;
    });
  };

  return (
    <Accordion openedItems={openedItems}>
      {evaluation.categories.map((category) => (
        <StyledAccordionItem
          key={category.ID}
          id={category.ID}
          borderBottom="1px solid"
          borderColor="graphic-default"
        >
          <AccordionHeader onClick={handleOnClick(category.ID)}>
            <EvaluationAccordionHeaderContent
              title={category.categoryName}
              points={category.questions.reduce(
                (total, question) => total + question.score.scorePoints,
                0
              )}
              maximumPoints={category.questions.reduce(
                (total, question) => total + question.score.totalPoints,
                0
              )}
              open={openedItems.has(category.ID)}
            />
          </AccordionHeader>
          <AccordionBody>
            {category.questions.map((question) => {
              if (isRatingEvaluationQuestion(question)) {
                return (
                  <QuestionMultiple key={question.ID} question={question} />
                );
              }

              if (isBinaryEvaluationQuestion(question)) {
                return <QuestionBinary key={question.ID} question={question} />;
              }

              throw new Error("Unknown question type.");
            })}
          </AccordionBody>
        </StyledAccordionItem>
      ))}
    </Accordion>
  );
}

export function EvaluationResult() {
  const navigate = useNavigate();

  const { callId } = useParams();

  const { t } = useTranslation();

  const { evaluation, loading } = useEvaluation(callId);
  const { call } = useCall();

  const revieweeUserId = getRevieweeUserId(call);
  const reviewer =
    evaluation?.reviewerTeammate?.entity.__typename === "PhoneTeammate"
      ? evaluation.reviewerTeammate.entity
      : null;

  const { updateEvaluation, loadingUpdateEvaluation } = useUpdateEvaluation();
  const { deleteEvaluation, loadingDeleteEvaluation } = useDeleteEvaluation();

  const handleMarkAsReviewed = (feedback: string) => {
    if (evaluation && revieweeUserId) {
      updateEvaluation(
        transformFormValueIntoInput({
          ID: evaluation.ID,
          feedback,
          revieweeUserId: revieweeUserId,
          categories: evaluation.categories,
        })
      );
    }
  };
  useTracking();

  const {
    value: isConfirmationModalOpen,
    setFalse: closeConfirmationModal,
    setTrue: openConfirmationModal,
  } = useBoolean();

  const editEvaluationHandler = () => {
    navigate("../edit");
  };

  const deleteEvaluationHandler = async () => {
    if (callId) {
      await deleteEvaluation({ callID: callId });
    }
  };
  const hasCcAccess = useHasCcAccess();

  if (!evaluation && !loading) {
    return <Navigate to="../empty" />;
  }

  return evaluation ? (
    <>
      <Flex flexDirection="column" data-test="evaluation-result-section">
        <EvaluationHeading
          title={evaluation.scoreCardName}
          totalPoints={evaluation.score.scorePoints}
          totalMaximumPoints={evaluation.score.totalPoints}
          comment={evaluation.feedback ?? ""}
          description={evaluation.scoreCardDescription}
          disabled={false}
          isReadonly={!hasCcAccess}
          onClickEdit={editEvaluationHandler}
          onClickDelete={openConfirmationModal}
          lastReviewAt={evaluation.updatedAt || evaluation.createdAt}
          lastReviewName={reviewer?.fullName}
        />

        {evaluation.isGeneratedByAI && !reviewer && (
          <MarkAsReviewedForm
            onSubmit={handleMarkAsReviewed}
            submitting={loadingUpdateEvaluation}
          />
        )}
        <Divider orientation="horizontal" h="1px" />
        <EvaluationAccordion evaluation={evaluation} />
      </Flex>
      <ConfirmationModal
        onHide={closeConfirmationModal}
        show={isConfirmationModalOpen}
        loading={loadingDeleteEvaluation}
        actions={[
          {
            label: t("cancel"),
            onClick: closeConfirmationModal,
          },
          {
            label: t("evaluation.remove_it"),
            onClick: deleteEvaluationHandler,
          },
        ]}
        title={t("evaluation.remove")}
        description={t("evaluation.cancel_evaluation_description", {
          scoreCardName: evaluation.scoreCardName,
        })}
      />
    </>
  ) : null;
}
